// pages/Dashboard.jsx

import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axiosInstance from "../../../configuration/request";
import DateFilter from "../../../component/dateFilter";
import MetricCard from "../components/metricCard";
import RevenueChart from "../components/revenueChart";
import ServicePopularity from "../components/servicePopularity";
import { useSnackbar } from "../../../component/SnackbarContext";
import dayjs from "dayjs";

/** API may return revenueByCampaign as an array of rows or a scalar total (string/number). */
function normalizeRevenueByCampaign(raw) {
  if (Array.isArray(raw)) {
    return raw.map((item, index) => ({
      name: item?.name || `Campaign ${index + 1}`,
      value: Number(item?.value ?? 0) || 0,
      details: Array.isArray(item?.details) ? item.details : [],
    }));
  }
  if (raw == null || raw === "") return [];
  if (typeof raw === "string" || typeof raw === "number") {
    const total = Number(raw);
    if (!Number.isFinite(total)) return [];
    if (total === 0) return [];
    return [{ name: "All campaigns", value: total, details: [] }];
  }
  if (typeof raw === "object") {
    return Object.entries(raw).map(([name, value]) => ({
      name,
      value: Number(value) || 0,
      details: [],
    }));
  }
  return [];
}

export default function CampaignMatrix() {
  const showSnackbar = useSnackbar();
  const { loginDetails } = useSelector((state) => state.counter);
  const storeId = loginDetails?.storeId || null;

  const [metrics, setMetrics] = useState([]);
  const [revenueTrends, setRevenueTrends] = useState([]);
  const [services, setServices] = useState([]);
  const [dateRange, setDateRange] = useState({
    startDate: dayjs().subtract(30, "day"),
    endDate: dayjs(),
  });


  useEffect(() => {
    if (storeId) fetchCampaign(dateRange.startDate, dateRange.endDate);
  }, [storeId, dateRange]);

  
  const fetchCampaign = (startDate, endDate) => {
    axiosInstance
      .get(`/customer/store/campaign-analytics/${storeId}`, {
      params: {
        startDate: startDate.startOf('day').toISOString(),
        endDate: endDate.endOf('day').toISOString(),
      },
    })
      .then((res) => {
        const data = res?.data || {};

        /* ---------- Metrics ---------- */
        setMetrics(Array.isArray(data.metrics) ? data.metrics : []);

        /* ---------- Redemption Trends (Chart) ---------- */
        const trendRows = Array.isArray(data.redemptionTrends)
          ? data.redemptionTrends
          : [];
        const trends = trendRows.map((item) => ({
          name: item.label,
          value: Number(item.value ?? 0) || 0,
          details: [],
        }));
        setRevenueTrends(trends);

        /* ---------- Revenue by Campaign ---------- */
        setServices(normalizeRevenueByCampaign(data.revenueByCampaign));
      })
      .catch((error) => {
        console.error("Error fetching campaign analytics", error);
        showSnackbar("Something went wrong!", "error");
      });
  };


   const handleRangeSelect = ({ startDate, endDate }) => {
    setDateRange({ startDate, endDate });
  };


  return (
    <div className="pt-1 pb-4 px-6 h-[calc(100vh-210px)] overflow-y-auto hide-scrollbar">
      {/* Date Filter */}
      <div className="flex justify-end mb-3">
        <DateFilter onSelectRange={handleRangeSelect} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Redemption Trend */}
        <div className="py-6 px-1 shadow-sm w-full border-2 border-[#CFDBE8] rounded-xl bg-[#F9FdFF]">
          <div className="mb-4 px-5">
            <div className="text-lg font-semibold text-black">
              Redemptions Over Time
            </div>
            <div className="text-3xl font-bold mt-1">
              {revenueTrends.reduce((sum, i) => sum + i.value, 0)}
            </div>
            <div className="text-sm text-[#4A739C] mt-1">Selected Period</div>
          </div>

          <RevenueChart data={revenueTrends} />
        </div>

        {/* Revenue by Campaign */}
        <div className="p-6 shadow-sm w-full border-2 border-[#CFDBE8] rounded-xl bg-[#F9FdFF]">
          <div className="mb-4">
            <div className="text-lg font-semibold text-black">
              Revenue by Campaign
            </div>
            <div className="text-3xl font-bold mt-1">
              $
              {services.reduce((sum, i) => sum + Number(i.value || 0), 0)}
            </div>
            <div className="text-sm text-[#4A739C] mt-1">
              Selected Period
            </div>
          </div>

          <ServicePopularity services={services} />
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 my-6">
        {metrics.map((item, i) => (
          <MetricCard key={i} {...item} />
        ))}
      </div>
    </div>
  );
}
